import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { Alert } from 'shared/ui'
import { sendActionTooltipProps } from 'widgets/MessageField/lib/sendActionTooltipProps'
import { type ChatbotDialogStore } from 'pages/chatbot/store/chatbotDialogStore'

import styles from './styles.module.scss'

type IMessageBoxWrapperProps = {
  dialog: ChatbotDialogStore
  children: React.ReactNode
  disabled?: boolean
}

type IAlertProps = {
  handleAction: () => void
  disabled?: boolean
  dialog: ChatbotDialogStore
  isExistChanges?: boolean
}

export const CompletedAlert = observer(({ handleAction, dialog, disabled }: IAlertProps) => {
  const completedReason = useMemo(() => {
    switch (dialog.status) {
      case 'completed':
        return 'Test completed. Success actions will be triggered for real contacts.'
      case 'abandoned':
      case 'terminated':
        return 'Test completed. Fallback actions will be triggered for real contacts.'
      default:
        return null
    }
  }, [dialog.status])

  if (!completedReason) return null

  return (
    <Alert
      item={{
        type: 'infoLight',
        desc: completedReason,
      }}
      rightActions={[
        {
          variant: 'button',
          type: 'primary',
          text: 'Restart',
          onClick: handleAction,
          disabled,
        },
      ]}
    />
  )
})

export const SaveAndRestartAlert = observer(
  ({ handleAction, dialog, disabled, isExistChanges }: IAlertProps) => {
    if (!isExistChanges && !dialog.invalid) return null
    if (dialog.completed) return null

    return (
      <Alert
        item={{
          type: 'infoLight',
          desc: 'To test the Agent, save and restart session',
        }}
        rightActions={[
          {
            variant: 'button',
            type: 'primary',
            text: 'Save & Restart',
            onClick: handleAction,
            disabled,
          },
        ]}
      />
    )
  }
)

export const MessageBoxWrapper = observer(
  ({ dialog, children, disabled }: IMessageBoxWrapperProps) => {
    const messageBoxDisabled = useMemo(
      () => disabled || dialog.invalid || !dialog.started || dialog.completed || dialog.loading,
      [disabled, dialog.invalid, dialog.started, dialog.completed, dialog.loading]
    )

    const sendButtonDisabled = useMemo(
      () => dialog.message.disablesSend || !dialog.started || dialog.loading || disabled,
      [dialog.message.disablesSend, dialog.started, dialog.loading, disabled]
    )

    useEffect(
      () => dialog.message.setDisabledMessageField(messageBoxDisabled),
      [messageBoxDisabled]
    )

    useEffect(() => {
      const handleSend = () => {
        dialog.send(dialog.message.text).then((sent) => {
          if (sent) dialog.message.setMessageText('', '')
        })
      }

      dialog.message.setSendActionBtnProps({
        onClick: handleSend,
        disabled: sendButtonDisabled,
        ariaLabel: 'SendTestMessage',
        tooltipProps: sendActionTooltipProps,
      })
    }, [sendButtonDisabled])

    return <footer className={styles.dialogFooter}>{children}</footer>
  }
)
